import { useBoolean } from '@lfg/core/hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAccount } from 'wagmi';

import { subgraphClientV1, subgraphClientV2 } from '@/agent/apiClient/api';
import { NODE_INVENTORIES_V1 } from '@/global/constants/nft.constant';
import type { NodeBalanceSGSchemaType, NodeBalanceSGV2SchemaType } from '@/global/schemas/subgraph.schema';
import type { Address, Nullable } from '@/global/types/common.type';
import { useFetchInventoriesV2Mutation } from '@/store/services/project.service';

import type { Inventory, InventoryV1Hook } from './types/inventory.type';

async function getNodeBlancesSGV1(address: Address): Promise<Nullable<Array<NodeBalanceSGSchemaType>>> {
  const query = `
    query GetNodeBalances($address: String!) {
      nodeBalances(where: { user: $address }) {
        balance
        id
        nft
        tokenId
        user
      }
    }
  `;

  try {
    const { data } = await subgraphClientV1({
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      data: {
        query,
        variables: { address },
      },
    });

    return data.data.nodeBalances;
  } catch (error) {
    return null;
  }
}

async function getNodeBlancesSGV2(address: Address): Promise<Nullable<Array<NodeBalanceSGV2SchemaType>>> {
  const query = `
    query GetNodeBalances($address: String!) {
      nodeBalances(where: { owner: $address }) {
        balance
        id
        node
        owner
      }
    }
  `;

  try {
    const { data } = await subgraphClientV2({
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      data: {
        query,
        variables: { address },
      },
    });
    return data.data.nodeBalances;
  } catch (error) {
    return null;
  }
}

export const useInventory = (): InventoryV1Hook => {
  const { value: isLoading, setTrue: setIsLoading, setFalse: setIsNotLoading } = useBoolean(false);
  const { address } = useAccount();
  const [inventoriesV1, setInventoriesV1] = useState<Array<Inventory>>([]);
  const [inventoriesV2, setInventoriesV2] = useState<Array<Inventory>>([]);
  const [fetchInventoriesV2] = useFetchInventoriesV2Mutation();

  const fetchInventoryV1 = useCallback(async () => {
    try {
      const balances = await getNodeBlancesSGV1(address);
      const inventoriesRes = NODE_INVENTORIES_V1.map(inventory => {
        const inventoryBalance = balances?.find(
          (balance: NodeBalanceSGSchemaType) => balance.nft === inventory.address,
        );
        return {
          ...inventory,
          ...inventoryBalance,
          balance: String(inventoryBalance?.balance) || '0',
        };
      });

      setInventoriesV1(inventoriesRes);
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
    }
  }, [address]);

  const fetchInventoryV2 = useCallback(async () => {
    if (!address) return;
    try {
      const balances = await getNodeBlancesSGV2(address);
      if (!balances) return;

      const data = {
        address,
        data: {
          nodes: balances.map((balance: NodeBalanceSGV2SchemaType) => balance.node) as string[],
        },
      };
      const inventoriesInfoRes = await fetchInventoriesV2(data);
      const inventoriesRes = balances.map((balance: NodeBalanceSGV2SchemaType) => {
        const nodeLower = balance.node.toLowerCase();
        const inventoryInfo = inventoriesInfoRes?.data[nodeLower];
        if (inventoryInfo) {
          return {
            ...balance,
            address: nodeLower as `0x${string}`,
            name: inventoryInfo.name,
            image: inventoryInfo.asset,
            balance: String(balance.balance) || '0',
          };
        }
        return undefined;
      });

      setInventoriesV2(inventoriesRes.filter(item => !!item) as Inventory[]);
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
    }
  }, [address]);

  const fetchInventory = useCallback(async () => {
    try {
      setIsLoading();
      await Promise.all([fetchInventoryV1(), fetchInventoryV2()]);
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
    } finally {
      setIsNotLoading();
    }
  }, [fetchInventoryV1, fetchInventoryV2, setIsLoading, setIsNotLoading]);

  const inventories = useMemo(() => {
    return inventoriesV1.concat(inventoriesV2);
  }, [inventoriesV1, inventoriesV2]);

  useEffect(() => {
    if (address) {
      fetchInventory();
    }
  }, [address, fetchInventory]);

  return {
    isLoading,
    inventories,
  };
};
